import * as Sentry from '@sentry/browser'
import { Integration } from '@sentry/core'
import { UserData, LoggerService, Context } from './Logger'

export class LoggerServiceSentry implements LoggerService {
  constructor({
    enableFeedbackWidget = false,
    enableUnmaskedReplays = false,
    ...init
  }: Sentry.BrowserOptions & { enableFeedbackWidget?: boolean; enableUnmaskedReplays?: boolean }) {
    if (!Sentry.getClient()) {
      const integrations: Integration[] = [
        Sentry.browserTracingIntegration() as Integration,
        Sentry.replayIntegration({
          // Mask by default, but allow selective unmasking with data-sentry-unmask attribute
          maskAllText: !enableUnmaskedReplays,
          blockAllMedia: !enableUnmaskedReplays,
          /*
            Allow for specific div elements to be masked/unmasked explicitly

            Usage examples:
              <div class="sentry-mask">This will be masked</div>
              <div data-sentry-mask>This will be masked</div>
              <div class="sentry-unmask">This will be unmasked</div>
              <div data-sentry-unmask>This will be unmasked</div>

           */
          // Unmask specific elements for debugging (IDs, statuses, error messages)
          unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
          // Explicitly mask PII (names, emails, account numbers)
          mask: ['.sentry-mask', '[data-sentry-mask]', 'input[type="email"]'],
        }) as Integration,
      ]

      if (enableFeedbackWidget) {
        integrations.push(
          Sentry.feedbackIntegration({
            triggerLabel: 'Got feedback?',
            formTitle: 'Got feedback?',
            messageLabel: 'Feedback',
            messagePlaceholder: 'Share your feedback or describe an issue',
            submitButtonLabel: 'Submit',
            showBranding: false,

            themeLight: {
              // easier to redefine colours than import them from the design system
              foreground: '#3a3c62',
              accentForeground: '#FFFFFF',
              accentBackground: '#207F82',
              outline: '#8F91A3',
              errorColor: '#aa0e43',
              successColor: '#2eb6b9',
              boxShadow: 'rgba(0, 0, 0, 0.16)',
            },
          })
        )
      }

      Sentry.init({
        environment: 'development',
        integrations,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        ...init,
      })
    }
  }

  setUserData(u: UserData): void {
    Sentry.setUser({
      id: u.id,
    })
  }

  logError(e: Error | undefined, context?: Context): void {
    Sentry.captureException(e, context)
  }
}
